/**
 * Applied Filters Label Component
 *
 * Label component for filter sections with optional count and expand arrow.
 * Figma: Applied Filters Label (Node: 51:7192)
 *
 * States:
 * - Default: Shows label, count text, optional "+X more" pill, and expand arrow (right-pointing chevron)
 * - Condensed: Shows label and collapse arrow (down-pointing dropdown)
 * - No Expand: Shows just the label
 */
"use client";

import Image from "next/image";

import { colors } from "@/styles/design-tokens";

export type AppliedFiltersLabelState = "default" | "condensed" | "no-expand";

interface AppliedFiltersLabelProps {
  label: string;
  state?: AppliedFiltersLabelState;
  filterCount?: number;
  brandCount?: number;
  /** Number of additional items not shown (for inline "+X more" pill on desktop collapsed state) */
  moreCount?: number;
  onExpandClick?: () => void;
  mobileActionLabel?: string;
  onMobileActionClick?: () => void;
  className?: string;
}

export function AppliedFiltersLabel({
  label,
  state = "default",
  filterCount = 0,
  brandCount = 0,
  moreCount = 0,
  onExpandClick,
  mobileActionLabel,
  onMobileActionClick,
  className = "",
}: AppliedFiltersLabelProps) {
  const mobileActionButton =
    mobileActionLabel && onMobileActionClick ? (
      <button
        type="button"
        onClick={onMobileActionClick}
        className="border-none bg-transparent p-0 text-action-muted text-ink-secondary lg:hidden"
      >
        {mobileActionLabel}
      </button>
    ) : null;

  // No Expand state: Just the label
  if (state === "no-expand") {
    return (
      <div className={`flex items-center justify-between gap-3 ${className}`}>
        <span className="text-tag-caps text-ink-secondary">{label}</span>
        {mobileActionButton}
      </div>
    );
  }

  // Condensed state: Label with dropdown arrow
  if (state === "condensed") {
    return (
      <div className={`flex items-center justify-between gap-3 ${className}`}>
        <div className="flex items-center gap-2">
          <span className="text-tag-caps text-ink-secondary">{label}</span>
          <button
            type="button"
            onClick={onExpandClick}
            className="flex items-center justify-center"
            style={{
              width: "14px",
              height: "14px",
            }}
            aria-label="Collapse"
          >
            <Image src="/icons/dropdown.svg" alt="" width={14} height={14} />
          </button>
        </div>

        {mobileActionButton}
      </div>
    );
  }

  // Default state: Label, count, optional "+X more" pill, and chevron arrow
  return (
    <div className={`flex items-center justify-between ${className}`} style={{ gap: "20px" }}>
      <div className="flex items-center gap-2">
        <span className="text-tag-caps text-ink-secondary">{label}</span>

        <button
          type="button"
          onClick={onExpandClick}
          className="flex items-center justify-center"
          style={{
            width: "14px",
            height: "14px",
          }}
          aria-label="Expand"
        >
          <Image src="/icons/chevron.svg" alt="" width={14} height={14} />
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className="text-action-muted text-ink-muted">
          {filterCount} {filterCount === 1 ? "filter" : "filters"}, {brandCount}{" "}
          {brandCount === 1 ? "brand" : "brands"}
        </span>

        {/* Inline "+X more" pill for desktop collapsed state - matches FacetButton summary variant */}
        {moreCount > 0 && (
          <span
            className="text-action inline-flex items-center gap-1 rounded-full text-ink-muted"
            style={{
              backgroundColor: colors.accent.lightestGray,
              paddingLeft: "10px",
              paddingRight: "10px",
              paddingTop: "3px",
              paddingBottom: "3px",
            }}
          >
            <Image src="/icons/plus.svg" alt="" width={10} height={10} style={{ opacity: 0.7 }} />
            {moreCount} more
          </span>
        )}

        {mobileActionButton}
      </div>
    </div>
  );
}
